import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Users, Clock, XCircle, Play, Square, UserPlus, AlertTriangle, Info } from 'lucide-react';
import { getStatusStyle } from './calendarUtils';
import { ShiftPeriodBadgeForShift, SHIFT_PERIODS, getShiftPeriod } from './ShiftPeriodBadge';
import { CalendarSleepoverPanel } from './CalendarSleepoverPanel';
import { useAppDialog } from '@/hooks/useAppDialog';
import { StaffSelectWithAvailability } from './StaffAvailabilityHelpers';
import { SilRatioSafetyPanel, RatioRulesScopeNote } from './SilRatioSafetyPanel';
import CalendarFanoutPanel from './CalendarFanoutPanel';
import { ServiceActivityBreakdownPanel } from './ServiceActivityBreakdownPanel';
import { SplitBillingChainBadge } from './SplitBillingChainBadge';

// Recommended canonical time ranges per shift period.
// Used by the drawer to surface a non-destructive "Apply recommended time"
// button when the admin picks a period that doesn't match the current times.
// SLEEP_OVER is intentionally absent — must be set manually.
const RECOMMENDED_TIMES = {
  MORNING:      { start: '06:00', end: '14:00' },
  AFTERNOON:    { start: '14:00', end: '18:00' },
  EVENING:      { start: '18:00', end: '22:00' },
  ACTIVE_NIGHT: { start: '22:00', end: '06:00' }, // crosses midnight
};

// Right-side drawer with full shift details + assignment + check-in/out + cancel.
export function CalendarShiftDrawer({ shift, open, onClose, staff, onUpdate, permissions }) {
  const { dangerConfirm } = useAppDialog();
  const [assigning, setAssigning] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [applyingTime, setApplyingTime] = useState(false);
  const [sleepoverFlagOn, setSleepoverFlagOn] = useState(false);
  const [timeConfirmOpen, setTimeConfirmOpen] = useState(false);

  // Escape-key support for the inline confirm modal.
  useEffect(() => {
    if (!timeConfirmOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setTimeConfirmOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [timeConfirmOpen]);

  const stopProp = (event) => { event.stopPropagation(); };
  const closeTimeConfirm = () => setTimeConfirmOpen(false);

  useEffect(() => {
    if (!open) return;
    api.get('/production/feature-flags/mine').then(res => {
      const flags = res.data?.data || res.data || {};
      setSleepoverFlagOn(!!flags.sleepover_billing_enabled);
    }).catch(() => setSleepoverFlagOn(false));
  }, [open]);

  if (!shift) return null;
  const style = getStatusStyle(shift.status);
  const canAssign = permissions.includes('roster.assign') || permissions.includes('roster.manage');
  const canEdit = permissions.includes('roster.update') || permissions.includes('roster.manage');
  const periodInfo = getShiftPeriod(shift);
  const periodSelectValue = shift.shift_period || 'AUTO';

  const handleAssign = async () => {
    if (!selectedStaff) return;
    setAssigning(true);
    try {
      // Soft availability pre-check (read-only — does NOT block).
      try {
        const checkRes = await api.get(`/availability/check?user_id=${selectedStaff}&date=${shift.date}&start_time=${shift.start_time}&end_time=${shift.end_time}`);
        const conflicts = checkRes.data?.data?.conflicts || [];
        if (conflicts.length > 0) {
          const labels = conflicts.map(c => c.label).join(', ');
          const ok = await dangerConfirm({
            title: 'Heads-up: availability conflict',
            description: `This staff member has marked ${shift.date} as ${labels}. Assigning anyway is allowed — please confirm before proceeding.`,
            confirmLabel: 'Assign anyway',
            cancelLabel: 'Pick someone else',
            variant: 'warning',
            testId: 'avail-conflict-confirm',
          });
          if (!ok) { setAssigning(false); return; }
        }
      } catch { /* check failure is non-blocking */ }

      await api.post(`/roster/shifts/${shift.id}/add-staff`, { staff_id: selectedStaff });
      toast.success('Staff assigned'); setSelectedStaff(''); onUpdate();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
    finally { setAssigning(false); }
  };
  const handleRemoveStaff = async (sid) => {
    try { await api.post(`/roster/shifts/${shift.id}/remove-staff`, { staff_id: sid }); toast.success('Removed'); onUpdate(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };
  const handleCancel = async () => {
    const result = await dangerConfirm({
      title: 'Cancel this shift?',
      description: 'This marks the shift as cancelled and notifies any assigned staff. The shift cannot be re-opened automatically.',
      confirmLabel: 'Yes, cancel shift',
      cancelLabel: 'Keep shift',
      reasonRequired: true,
      testId: 'cancel-shift-confirm',
    });
    if (!result) return;
    try {
      await api.post(`/roster/shifts/${shift.id}/cancel`, { reason: result.reason || 'Cancelled from scheduling board' });
      toast.success('Cancelled'); onUpdate(); onClose();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };
  const handleCheckIn = async () => {
    try { await api.post(`/roster/shifts/${shift.id}/check-in`, {}); toast.success('Checked in'); onUpdate(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };
  const handleCheckOut = async () => {
    try { await api.post(`/roster/shifts/${shift.id}/check-out`, {}); toast.success('Checked out'); onUpdate(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };
  const handleAdminCloseMissedClockout = async () => {
    const result = await dangerConfirm({
      title: 'Close missed clock-out?',
      description: "The support worker forgot to clock out. We'll mark the shift COMPLETED, set check-out to its scheduled end, and submit the timesheet for approval. This is fully audit-logged.",
      confirmLabel: 'Close shift & submit timesheet',
      cancelLabel: 'Keep open',
      reasonRequired: true,
      testId: 'admin-close-missed-clockout-confirm',
    });
    if (!result) return;
    try {
      await api.post(`/roster/shifts/${shift.id}/admin-close-missed-clockout`, {
        reason: result.reason || 'Admin closed missed clock-out',
      });
      toast.success('Shift closed and timesheet submitted for approval');
      onUpdate();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    }
  };

  const handlePeriodChange = async (newValue) => {
    if (!canEdit) return;
    setSavingPeriod(true);
    try {
      await api.put(`/roster/shifts/${shift.id}`, { shift_period: newValue === 'AUTO' ? null : newValue });
      toast.success(newValue === 'AUTO' ? 'Shift period set to auto' : `Shift period: ${newValue.replace('_', ' ')}`);
      onUpdate();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally { setSavingPeriod(false); }
  };

  // Recommendation helpers — purely advisory; never mutate times unless the
  // admin clicks "Apply recommended time".
  const currentPeriod = shift.shift_period;
  const recommended = currentPeriod ? RECOMMENDED_TIMES[currentPeriod] : null;
  const timesMatchRecommendation = recommended
    ? shift.start_time === recommended.start && shift.end_time === recommended.end
    : true;
  const periodPrettyLabel = currentPeriod ? currentPeriod.replace('_', ' ').toLowerCase() : '';

  const handleApplyRecommendedTime = async () => {
    if (!canEdit || !recommended) return;
    // Open the AppConfirmModal — actual API call happens in handleConfirmApply
    setTimeConfirmOpen(true);
  };

  const handleConfirmApply = async () => {
    if (!canEdit || !recommended) return;
    setApplyingTime(true);
    try {
      await api.put(`/roster/shifts/${shift.id}`, {
        start_time: recommended.start,
        end_time: recommended.end,
      });
      toast.success(`Shift times updated to ${recommended.start} – ${recommended.end}`);
      setTimeConfirmOpen(false);
      onUpdate();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally { setApplyingTime(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[440px] overflow-y-auto" data-testid="shift-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Shift Details</SheetTitle>
          <SheetDescription>View, assign staff, manage attendance and cancel this shift</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          {/* Phase A — SIL ratio safety panel (renders only for shifts with support_ratio or multi-participant) */}
          <SilRatioSafetyPanel shift={shift} />
          {(shift.support_ratio || (shift.linked_participants && shift.linked_participants.length > 1)) && (
            <RatioRulesScopeNote />
          )}
          {/* Phase B — Fan-Out panel (only renders for 1:2 shared-support shifts) */}
          <CalendarFanoutPanel shift={shift} onChanged={onUpdate} />
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${style.bg} ${style.text} border`}>{shift.status}</Badge>
            <ShiftPeriodBadgeForShift shift={shift} size="md" />
            {shift.check_in_time && !shift.check_out_time && <Badge variant="outline" className="text-sky-600 border-sky-300">In Progress</Badge>}
          </div>
          {canEdit && (
            <Card>
              <CardContent className="p-3 space-y-1.5">
                <p className="text-xs text-muted-foreground">Shift Period {periodInfo.auto && <span className="text-[10px]">(auto-classified)</span>}</p>
                <Select value={periodSelectValue} onValueChange={handlePeriodChange} disabled={savingPeriod}>
                  <SelectTrigger className="h-8 text-xs" data-testid="drawer-shift-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AUTO">Auto-detect</SelectItem>
                    {SHIFT_PERIODS.map(p => <SelectItem key={p} value={p}>{p.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Recommendation panel — advisory only */}
                {currentPeriod === 'SLEEP_OVER' && (
                  <p
                    className="text-[10px] text-muted-foreground pt-1"
                    data-testid="period-sleepover-manual-note"
                  >
                    Sleep-Over uses manual times only — no recommendation applied.
                  </p>
                )}

                {recommended && !timesMatchRecommendation && (
                  <div
                    className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1.5"
                    data-testid="period-time-mismatch-warning"
                  >
                    <div className="flex items-start gap-1.5 text-[11px] text-amber-900">
                      <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                      <div>
                        Current time <span className="font-semibold tabular-nums">{shift.start_time}–{shift.end_time}</span>{' '}
                        does not match {periodPrettyLabel} (recommended{' '}
                        <span className="font-semibold tabular-nums">{recommended.start}–{recommended.end}</span>).
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] w-full border-amber-400 text-amber-900 hover:bg-amber-100"
                      onClick={handleApplyRecommendedTime}
                      disabled={applyingTime}
                      data-testid="period-apply-recommended-time-btn"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {applyingTime ? 'Applying…' : `Apply recommended time (${recommended.start}–${recommended.end})`}
                    </Button>
                  </div>
                )}

                <p className="text-[10px] text-muted-foreground">
                  Shift Period helps classify the shift. You can apply the recommended time or keep custom times.
                </p>
              </CardContent>
            </Card>
          )}
          <Card><CardContent className="p-3 space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><p className="text-muted-foreground text-xs">Date</p><p className="font-medium">{shift.date}</p></div>
              <div><p className="text-muted-foreground text-xs">Time</p><p className="font-medium">{shift.start_time} – {shift.end_time}</p></div>
            </div>
            <Separator />
            <div><p className="text-muted-foreground text-xs">Participant</p><p className="font-medium">{shift.participant_name || '—'}</p></div>
            <div><p className="text-muted-foreground text-xs">Service</p><p className="font-medium">{shift.service_name || '—'}</p></div>
            {shift.house_name && <div><p className="text-muted-foreground text-xs">House</p><p className="font-medium">{shift.house_name}</p></div>}
          </CardContent></Card>

          <Card><CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Staff</CardTitle></CardHeader>
            <CardContent className="p-3 pt-0 space-y-2">
              {shift.assigned_staff?.length > 0 ? shift.assigned_staff.map(s => (
                <div key={s.id} className="flex items-center justify-between bg-slate-50 rounded-md p-2">
                  <span className="text-sm font-medium">{s.name}</span>
                  {canAssign && <Button size="sm" variant="ghost" className="text-red-500 h-6 text-xs" onClick={() => handleRemoveStaff(s.id)}>Remove</Button>}
                </div>
              )) : <p className="text-xs text-muted-foreground">No staff assigned</p>}
              {canAssign && (shift.assigned_staff?.length || 0) < (shift.required_staff || 1) && (
                <div className="flex gap-2 pt-1">
                  <StaffSelectWithAvailability
                    staff={staff}
                    value={selectedStaff}
                    onValueChange={setSelectedStaff}
                    triggerClass="flex-1 h-8 text-xs"
                    testId="drawer-assign-staff-select"
                    filter={(s) => !shift.assigned_staff?.find(a => a.id === (s.user_id || s.id))}
                    date={shift.date}
                    startTime={shift.start_time}
                    endTime={shift.end_time}
                    excludeShiftId={shift.id}
                  />
                  <Button size="sm" className="h-8" onClick={handleAssign} disabled={assigning || !selectedStaff}><UserPlus className="w-3.5 h-3.5" /></Button>
                </div>
              )}
            </CardContent>
          </Card>

          {(shift.check_in_time || ['ASSIGNED', 'CONFIRMED', 'IN_PROGRESS'].includes(shift.status)) && shift.assigned_staff?.length > 0 && (
            <Card><CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Attendance</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {shift.check_in_time && <div className="text-xs"><span className="text-muted-foreground">In:</span> {new Date(shift.check_in_time).toLocaleString()}</div>}
                {shift.check_out_time && <div className="text-xs"><span className="text-muted-foreground">Out:</span> {new Date(shift.check_out_time).toLocaleString()}</div>}
                <div className="flex gap-2">
                  {!shift.check_in_time && ['ASSIGNED', 'CONFIRMED'].includes(shift.status) && (
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={handleCheckIn}><Play className="w-3 h-3 mr-1" /> Check In</Button>
                  )}
                  {shift.check_in_time && !shift.check_out_time && (
                    <Button size="sm" variant="destructive" className="flex-1 h-7 text-xs" onClick={handleCheckOut}><Square className="w-3 h-3 mr-1" /> Check Out</Button>
                  )}
                </div>
                {canEdit && shift.check_in_time && !shift.check_out_time && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={handleAdminCloseMissedClockout}
                    data-testid="admin-close-missed-clockout-button"
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" /> Close missed clock-out
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {sleepoverFlagOn && shift.shift_period === 'SLEEP_OVER' && (
            <CalendarSleepoverPanel shift={shift} onUpdate={onUpdate} canEdit={canEdit} />
          )}

          {/* Phase 1.5 — Admin-only operational service breakdown.
              Does NOT affect invoicing, payroll, or claims. */}
          {canEdit && (
            <>
              <ServiceActivityBreakdownPanel shift={shift} onChange={onUpdate} />
              {/* Phase 3F.5 — split-billing chain status badge (dev mode) */}
              <div className="mt-2">
                <SplitBillingChainBadge shiftId={shift.id} />
              </div>
            </>
          )}

          {canEdit && !['COMPLETED', 'CANCELLED', 'INVOICED'].includes(shift.status) && (
            <Button size="sm" variant="outline" className="w-full h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleCancel}>
              <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel Shift
            </Button>
          )}
        </div>
      </SheetContent>

      {/* Apply Recommended Time confirmation — replaces native window.confirm() */}
      {recommended && timeConfirmOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="time-confirm-title"
          data-testid="shift-period-time-confirm"
          onClick={closeTimeConfirm}
        >
          <div
            className="w-full max-w-[460px] rounded-2xl bg-background shadow-2xl border p-5"
            onClick={stopProp}
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 rounded-full p-2 bg-slate-100">
                <Info className="w-5 h-5 text-slate-600" aria-hidden="true" />
              </div>
              <div className="space-y-1 pt-0.5 flex-1">
                <h2 id="time-confirm-title" className="text-base font-semibold leading-tight">
                  Update shift time?
                </h2>
                {currentPeriod && (
                  <p className="text-sm text-muted-foreground">
                    You selected {periodPrettyLabel}.
                  </p>
                )}
              </div>
            </div>
            <div className="text-sm space-y-2 pt-3">
              <div className="rounded-md border bg-muted/30 p-2.5 text-xs space-y-1">
                <div className="text-muted-foreground">Time</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Current</div>
                    <div className="font-medium tabular-nums">{shift.start_time}–{shift.end_time}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">After</div>
                    <div className="font-semibold tabular-nums text-emerald-700">
                      {recommended.start}–{recommended.end}
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-md border px-2.5 py-2 text-[11px] leading-relaxed bg-blue-50 border-blue-200 text-blue-900">
                This will not affect billing, payroll, invoices, claims, or funding.
              </div>
              <div className="flex justify-end gap-2 pt-3">
                <Button
                  variant="outline"
                  onClick={() => setTimeConfirmOpen(false)}
                  disabled={applyingTime}
                  data-testid="shift-period-time-confirm-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmApply}
                  disabled={applyingTime}
                  data-testid="shift-period-time-confirm-confirm"
                >
                  {applyingTime ? 'Working…' : 'Apply Change'}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Sheet>
  );
}
